import { createAction, type QueuedAction } from "@/v2/hooks/useActionQueue";
import { formatEntryId } from "./entryId";
import {
	type ActionDeps,
	ApiError,
	actingHeaders,
	combineRollback,
	refreshBoth,
} from "./types";

export interface MergeOptions {
	matterId?: string | null;
	date?: string;
	taskId?: string | null;
	activityId?: string | null;
	unitsSeconds?: number;
}

function stripUndefined<T extends object>(obj: T): Partial<T> {
	return Object.fromEntries(
		Object.entries(obj).filter(([, value]) => value !== undefined),
	) as Partial<T>;
}

export function mergeAction(
	ids: number[],
	options: MergeOptions,
	deps: ActionDeps,
	onApiError?: (err: ApiError) => void,
	timekeeperId?: string | null,
): QueuedAction {
	// /v2/entries/merge creates a brand-new entry and supersedes all sources,
	// so the optimistic update removes every source row from both stores. The
	// refresh pulls in the newly created entry on success.
	const sourceSet = new Set(ids);
	const v2SourceSet = new Set(ids.map(formatEntryId));
	const rollbackLegacy = deps.updateEntries((prev) =>
		prev.filter((e) => !sourceSet.has(e.id)),
	);
	const rollbackV2 =
		deps.updateEntriesV2?.((prev) =>
			prev.filter((e) => !v2SourceSet.has(e.id)),
		) ?? null;
	const rollback = combineRollback(rollbackLegacy, rollbackV2);

	return createAction({
		execute: async () => {
			const resp = await deps.makeApiRequest({
				endpoint: "/v2/entries/merge",
				method: "POST",
				body: {
					sourceIds: ids.map(formatEntryId),
					...stripUndefined(options),
				},
				headers: actingHeaders(timekeeperId),
			});
			if (resp.status === "ERROR") {
				if (resp.apiError) {
					const apiError = new ApiError(resp.apiError);
					onApiError?.(apiError);
					throw apiError;
				}
				throw new Error(resp.error || "Request failed");
			}
		},
		rollback,
		toast: {
			loading: `Merging ${ids.length} entries…`,
			success: `Merged ${ids.length} entries`,
			error: "Failed to merge entries",
		},
		onSuccess: () => refreshBoth(deps),
		onError: () => refreshBoth(deps),
	});
}
